import type { Span } from '@opentelemetry/api';
import { context, SpanKind, SpanStatusCode, trace } from '@opentelemetry/api';

// Default build info that can be overridden
export const DEFAULT_BUILD_INFO = {
  'service.name': 'unknown-service',
  'service.version': '0.0.0',
  'service.environment': 'development',
} as const;

// Allow consumers to set their own build info
let customBuildInfo: Record<string, string | number | boolean> = {};

export function setBuildInfo(
  buildInfo: Record<string, string | number | boolean>
) {
  customBuildInfo = buildInfo;
}

export function getBuildInfo(): Record<string, string | number | boolean> {
  return { ...DEFAULT_BUILD_INFO, ...customBuildInfo };
}

/**
 * Initialize build info from a generated BUILD_INFO constant
 * This is typically called at application startup
 */
export function initializeBuildInfo(
  buildInfo: Record<string, string | number | boolean>
) {
  setBuildInfo(buildInfo);
}

/**
 * Data categories we can track in wide events
 */
export interface UserData {
  /** User identifier */
  id?: string;
  /** User email address */
  email?: string;
  /** User's first name */
  firstName?: string;
  /** User's last name */
  lastName?: string;
  /** Type of actor (user, service, system, etc.) */
  actorType?: string;
  /** Additional user properties */
  [key: string]: string | number | boolean | undefined;
}

export interface EntityData {
  /** Type of entity (user, order, product, etc.) */
  type: string;
  /** Entity identifier */
  id: string;
  /** Entity-specific properties */
  [key: string]: string | number | boolean | undefined;
}

export interface OperationData {
  /** Domain operation being performed */
  domain?: string;
  /** Operation layer (validation, business, persistence, etc.) */
  layer?: string;
  /** Operation-specific properties */
  [key: string]: string | number | boolean | undefined;
}

export interface InfrastructureData {
  /** Database operation details */
  database?: {
    operation?: string;
    table?: string;
    query?: string;
    duration?: number;
  };
  /** Event store details */
  eventStore?: {
    streamId?: string;
    expectedVersion?: number;
    nextVersion?: number;
    createdNewStream?: boolean;
  };
  /** External service calls */
  externalService?: {
    name?: string;
    operation?: string;
    duration?: number;
  };
  /** Custom infrastructure properties */
  [key: string]: string | number | boolean | object | undefined;
}

export interface ErrorData {
  /** Error type/category */
  type?: string;
  /** Error message */
  message?: string;
  /** Validation-specific error details */
  validation?: {
    issueCount?: number;
    fields?: string[];
  };
  /** Business rule violation details */
  businessRule?: {
    rule?: string;
    violation?: string;
  };
  /** Whether this was an unexpected error */
  unexpected?: boolean;
  /** Additional error properties */
  [key: string]: string | number | boolean | object | undefined;
}

/**
 * Simplified Wide Events wrapper for comprehensive observability.
 *
 * This class provides a fluent API for tracking different types of context
 * in your application operations. It automatically handles OpenTelemetry
 * span management and converts everything to structured wide events.
 *
 * Key methods:
 * - `setOperation()` - Set the operation type and context (e.g., 'user.create')
 * - `trackUser()` - Track user/actor information
 * - `trackEntity()` - Track the primary entity being operated on
 * - `trackInfrastructure()` - Track technical details like database operations
 * - `trackError()` - Track error information
 *
 * @example
 * ```typescript
 * const tracker = new WideEventsWrapper('Create User', 'user.create');
 *
 * await tracker.execute(async (t) => {
 *   t.setOperation('user.create', { domain: 'user-management', layer: 'api' })
 *    .trackUser({ id: '123', email: 'john@example.com' })
 *    .trackEntity({ type: 'user', id: '123' });
 *
 *   // Your business logic here
 * });
 * ```
 */
export class WideEventsWrapper {
  private requestId: string;
  private rootSpan: Span;
  private trackedData: Record<string, string | number | boolean> = {};
  private hasError = false;

  /**
   * Creates a new wide events wrapper for an operation.
   *
   * @param operationName - Human-readable name for the operation (e.g., "Create User")
   * @param operationType - Machine-readable operation type (e.g., "user.create")
   * @param serviceName - Name of the service (defaults to environment variable)
   */
  constructor(
    operationName: string,
    operationType: string,
    serviceName: string = process.env.SERVICE_NAME || 'unknown-service'
  ) {
    this.requestId = crypto.randomUUID();

    const buildInfo = getBuildInfo();
    const tracer = trace.getTracer(
      serviceName,
      buildInfo['service.version'] as string
    );

    this.rootSpan = tracer.startSpan(operationName, {
      kind: SpanKind.SERVER,
      attributes: {
        'request.id': this.requestId,
        'operation.type': operationType,
        'operation.name': operationName,
        ...buildInfo,
      },
    });

    this.trackedData = {
      request_id: this.requestId,
      operation: operationType,
    };
  }

  /**
   * Set the operation type and context for this operation.
   *
   * This is the primary method for identifying what operation is being performed.
   * It sets both the operation type identifier and any contextual metadata.
   *
   * @param operationType - The operation type identifier (e.g., "user.create", "user.change-password")
   * @param context - Optional contextual information about the operation
   * @returns This instance for method chaining
   *
   * @example
   * ```typescript
   * // Simple operation
   * tracker.setOperation('user.create');
   *
   * // With context
   * tracker.setOperation('user.create', {
   *   domain: 'user-management',
   *   layer: 'api'
   * });
   *
   * // With custom properties
   * tracker.setOperation('user.change-password', {
   *   domain: 'user-management',
   *   layer: 'business-logic',
   *   trigger: 'user-initiated'
   * });
   * ```
   */
  setOperation(operationType: string, context?: OperationData): this {
    const attributes: Record<string, string | number | boolean> = {
      'operation.type': operationType,
    };

    // Add context if provided
    if (context) {
      if (context.domain) attributes['operation.domain'] = context.domain;
      if (context.layer) attributes['operation.layer'] = context.layer;

      // Add custom operation properties
      for (const [key, value] of Object.entries(context)) {
        if (value !== undefined && !['domain', 'layer'].includes(key)) {
          attributes[`operation.${key}`] = value;
        }
      }
    }

    this.rootSpan.setAttributes(attributes);
    Object.assign(this.trackedData, {
      operation: operationType,
      ...attributes,
    });

    return this;
  }

  /**
   * Track user/actor information for this operation.
   *
   * Use this to record who is performing the operation, including
   * user details, authentication context, and actor type.
   *
   * @param userData - User and actor information
   * @returns This instance for method chaining
   *
   * @example
   * ```typescript
   * tracker.trackUser({
   *   id: '123',
   *   email: 'john@example.com',
   *   actorType: 'authenticated_user'
   * });
   * ```
   */
  trackUser(userData: UserData): this {
    const attributes: Record<string, string | number | boolean> = {};

    if (userData.id) attributes['user.id'] = userData.id;
    if (userData.email) attributes['user.email'] = userData.email;
    if (userData.firstName) attributes['user.first_name'] = userData.firstName;
    if (userData.lastName) attributes['user.last_name'] = userData.lastName;
    if (userData.actorType) attributes['actor.type'] = userData.actorType;

    // Add custom user properties
    for (const [key, value] of Object.entries(userData)) {
      if (
        value !== undefined &&
        !['id', 'email', 'firstName', 'lastName', 'actorType'].includes(key)
      ) {
        attributes[`user.${key}`] = value;
      }
    }

    this.rootSpan.setAttributes(attributes);
    Object.assign(this.trackedData, attributes);

    return this;
  }

  /**
   * Track the primary entity/resource being operated on.
   *
   * Use this to record what thing your operation is working with,
   * such as a user being created, an order being processed, etc.
   *
   * @param entityData - Entity/resource information
   * @returns This instance for method chaining
   *
   * @example
   * ```typescript
   * tracker.trackEntity({
   *   type: 'user',
   *   id: 'user-123',
   *   status: 'active'
   * });
   * ```
   */
  trackEntity(entityData: EntityData): this {
    const attributes: Record<string, string | number | boolean> = {
      'entity.type': entityData.type,
      'entity.id': entityData.id,
    };

    // Add custom entity properties
    for (const [key, value] of Object.entries(entityData)) {
      if (value !== undefined && !['type', 'id'].includes(key)) {
        attributes[`entity.${key}`] = value;
      }
    }

    this.rootSpan.setAttributes(attributes);
    Object.assign(this.trackedData, attributes);

    return this;
  }

  /**
   * Track infrastructure and technical details.
   *
   * Use this to record technical aspects of your operation,
   * such as database operations, external service calls, etc.
   *
   * @param infrastructureData - Infrastructure operation details
   * @returns This instance for method chaining
   *
   * @example
   * ```typescript
   * tracker.trackInfrastructure({
   *   database: {
   *     operation: 'INSERT',
   *     table: 'users',
   *     duration: 45
   *   },
   *   eventStore: {
   *     streamId: 'user-123',
   *     expectedVersion: 0
   *   }
   * });
   * ```
   */
  trackInfrastructure(infrastructureData: InfrastructureData): this {
    const attributes: Record<string, string | number | boolean> = {};

    // Handle database details
    if (infrastructureData.database) {
      const db = infrastructureData.database;
      if (db.operation) attributes['db.operation'] = db.operation;
      if (db.table) attributes['db.table'] = db.table;
      if (db.query) attributes['db.query'] = db.query;
      if (db.duration) attributes['db.duration_ms'] = db.duration;
    }

    // Handle event store details
    if (infrastructureData.eventStore) {
      const es = infrastructureData.eventStore;
      if (es.streamId) attributes['event_store.stream_id'] = es.streamId;
      if (es.expectedVersion !== undefined)
        attributes['event_store.expected_version'] = es.expectedVersion;
      if (es.nextVersion !== undefined)
        attributes['event_store.next_version'] = es.nextVersion;
      if (es.createdNewStream !== undefined)
        attributes['event_store.created_new_stream'] = es.createdNewStream;
    }

    // Handle external service details
    if (infrastructureData.externalService) {
      const ext = infrastructureData.externalService;
      if (ext.name) attributes['external_service.name'] = ext.name;
      if (ext.operation)
        attributes['external_service.operation'] = ext.operation;
      if (ext.duration)
        attributes['external_service.duration_ms'] = ext.duration;
    }

    // Handle custom infrastructure properties
    for (const [key, value] of Object.entries(infrastructureData)) {
      if (
        value !== undefined &&
        !['database', 'eventStore', 'externalService'].includes(key)
      ) {
        if (
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean'
        ) {
          attributes[`infrastructure.${key}`] = value;
        }
      }
    }

    this.rootSpan.setAttributes(attributes);
    Object.assign(this.trackedData, attributes);

    return this;
  }

  /**
   * Track error information.
   *
   * Use this to record error details, including error type,
   * validation failures, business rule violations, etc.
   *
   * @param errorData - Error information
   * @returns This instance for method chaining
   *
   * @example
   * ```typescript
   * tracker.trackError({
   *   type: 'ValidationError',
   *   message: 'Email is required',
   *   validation: {
   *     issueCount: 2,
   *     fields: ['email', 'firstName']
   *   }
   * });
   * ```
   */
  trackError(errorData: ErrorData): this {
    const attributes: Record<string, string | number | boolean> = {};

    if (errorData.type) attributes['error.type'] = errorData.type;
    if (errorData.message) attributes['error.message'] = errorData.message;
    if (errorData.unexpected !== undefined)
      attributes['error.unexpected'] = errorData.unexpected;

    // Handle validation errors
    if (errorData.validation) {
      const val = errorData.validation;
      if (val.issueCount)
        attributes['error.validation.issue_count'] = val.issueCount;
      if (val.fields)
        attributes['error.validation.fields'] = val.fields.join(',');
    }

    // Handle business rule violations
    if (errorData.businessRule) {
      const rule = errorData.businessRule;
      if (rule.rule) attributes['error.business_rule.rule'] = rule.rule;
      if (rule.violation)
        attributes['error.business_rule.violation'] = rule.violation;
    }

    // Handle custom error properties
    for (const [key, value] of Object.entries(errorData)) {
      if (
        value !== undefined &&
        ![
          'type',
          'message',
          'unexpected',
          'validation',
          'businessRule',
        ].includes(key)
      ) {
        if (
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean'
        ) {
          attributes[`error.${key}`] = value;
        }
      }
    }

    this.rootSpan.setAttributes(attributes);
    Object.assign(this.trackedData, attributes);

    // Also record as OpenTelemetry error
    this.rootSpan.setStatus({
      code: SpanStatusCode.ERROR,
      message: errorData.message || 'Operation failed',
    });

    // Mark that an error has been tracked
    this.hasError = true;

    return this;
  }

  /**
   * Record a business event during the operation.
   *
   * Use this to record significant business events that occur
   * during your operation, such as "validation.completed" or
   * "user.created".
   *
   * @param eventName - Name of the business event
   * @param eventData - Additional event data
   * @returns This instance for method chaining
   *
   * @example
   * ```typescript
   * tracker.recordEvent('user.created', {
   *   userId: '123',
   *   source: 'api'
   * });
   * ```
   */
  recordEvent(
    eventName: string,
    eventData: Record<string, string | number | boolean> = {}
  ): this {
    this.rootSpan.addEvent(eventName, eventData);
    return this;
  }

  /**
   * Create a child span for a sub-operation.
   *
   * Use this to create detailed spans for specific parts of your
   * operation, such as validation, database operations, etc.
   *
   * @param name - Name of the sub-operation
   * @param attributes - Additional span attributes
   * @returns OpenTelemetry span for the sub-operation
   *
   * @example
   * ```typescript
   * const validationSpan = tracker.createOperationSpan('validation', {
   *   'operation.layer': 'validation'
   * });
   *
   * try {
   *   // validation logic
   *   validationSpan.setStatus({ code: SpanStatusCode.OK });
   * } catch (error) {
   *   validationSpan.recordException(error);
   * } finally {
   *   validationSpan.end();
   * }
   * ```
   */
  createOperationSpan(
    name: string,
    attributes: Record<string, string | number | boolean> = {}
  ): Span {
    const buildInfo = getBuildInfo();
    const tracer = trace.getTracer(
      process.env.SERVICE_NAME || 'unknown-service',
      buildInfo['service.version'] as string
    );

    return tracer.startSpan(
      name,
      {
        kind: SpanKind.INTERNAL,
        attributes,
      },
      trace.setSpan(context.active(), this.rootSpan)
    );
  }

  /**
   * Automatically track HTTP request details.
   *
   * This method extracts and tracks HTTP-specific information
   * from the request, including method, URL, headers, etc.
   *
   * @param request - HTTP request object
   * @returns This instance for method chaining
   *
   * @internal This method is typically called by middleware
   */
  trackHttpRequest(request: Request): this {
    const url = new URL(request.url);

    this.rootSpan.setAttributes({
      'http.method': request.method,
      'http.url': request.url,
      'http.scheme': url.protocol.slice(0, -1),
      'http.host': url.host,
      'http.target': url.pathname + url.search,
      'http.user_agent': request.headers.get('user-agent') || '',
      'http.request_content_length':
        request.headers.get('content-length') || '',
    });

    this.rootSpan.addEvent('http.request_received', {
      'http.method': request.method,
      'http.path': url.pathname,
    });

    return this;
  }

  /**
   * Automatically track HTTP response details.
   *
   * This method extracts and tracks HTTP response information,
   * including status code, headers, and sets appropriate span status.
   *
   * @param response - HTTP response object
   * @returns This instance for method chaining
   *
   * @internal This method is typically called by middleware
   */
  trackHttpResponse(response: Response): this {
    this.rootSpan.setAttributes({
      'http.status_code': response.status,
      'http.status_text': response.statusText,
      'http.response_content_length':
        response.headers.get('content-length') || '',
    });

    this.trackedData['http.status_code'] = response.status;

    // Only set span status if it hasn't been set by trackError()
    // This prevents overwriting detailed error messages with generic HTTP status
    if (!this.hasError) {
      if (response.status >= 400) {
        this.rootSpan.setStatus({
          code: SpanStatusCode.ERROR,
          message: `HTTP ${response.status}`,
        });
      } else {
        this.rootSpan.setStatus({ code: SpanStatusCode.OK });
      }
    } else {
      // Even if error was already tracked, we still need to set success for 2xx responses
      if (response.status >= 200 && response.status < 300) {
        this.rootSpan.setStatus({ code: SpanStatusCode.OK });
        this.hasError = false; // Reset error flag for successful responses
      }
    }

    this.rootSpan.addEvent('http.response_sent', {
      'http.status_code': response.status,
      success: response.status >= 200 && response.status < 300,
    });

    return this;
  }

  /**
   * Execute the operation with proper OpenTelemetry context.
   *
   * This method wraps your operation in the appropriate OpenTelemetry
   * context and ensures spans are properly managed and exported.
   *
   * @param fn - Operation function to execute
   * @returns Promise with the operation result
   *
   * @example
   * ```typescript
   * const result = await tracker.execute(async (t) => {
   *   t.trackUser({ id: '123' });
   *   // Your operation logic here
   *   return { success: true };
   * });
   * ```
   */
  async execute<R>(fn: (wrapper: this) => Promise<R>): Promise<R> {
    return context.with(
      trace.setSpan(context.active(), this.rootSpan),
      async () => {
        try {
          const result = await fn(this);

          // Only set success if no HTTP status was set
          if (!this.trackedData['http.status_code']) {
            this.rootSpan.setStatus({ code: SpanStatusCode.OK });
          }

          return result;
        } catch (error) {
          if (error instanceof Error) {
            this.rootSpan.recordException(error);
            this.rootSpan.setStatus({
              code: SpanStatusCode.ERROR,
              message: error.message,
            });
          }
          throw error;
        } finally {
          this.rootSpan.end();
        }
      }
    );
  }

  /**
   * Get all tracked data for this operation.
   *
   * @returns Copy of all tracked data
   *
   * @internal Primarily used for testing and debugging
   */
  getTrackedData(): Record<string, string | number | boolean> {
    return { ...this.trackedData };
  }
}

/**
 * Factory function to create a wide events wrapper for user operations.
 *
 * @param operationName - Specific operation name (e.g., "create", "update")
 * @returns Configured wide events wrapper
 *
 * @example
 * ```typescript
 * const tracker = createUserOperationTracker('create');
 * ```
 */
export function createUserOperationTracker(
  operationName: string
): WideEventsWrapper {
  return new WideEventsWrapper(
    `User ${operationName}`,
    `user.${operationName.toLowerCase()}`
  );
}

/**
 * Factory function to create a generic wide events wrapper.
 *
 * @param entity - Entity type (e.g., "user", "order")
 * @param operation - Operation name (e.g., "create", "update")
 * @returns Configured wide events wrapper
 *
 * @example
 * ```typescript
 * const tracker = createOperationTracker('order', 'create');
 * ```
 */
export function createOperationTracker(
  entity: string,
  operation: string
): WideEventsWrapper {
  return new WideEventsWrapper(
    `${entity} ${operation}`,
    `${entity.toLowerCase()}.${operation.toLowerCase()}`
  );
}
